import { useContext } from 'react';
import type { FunctionComponent } from 'react';
import { useTranslation } from 'i18n';
import { Box } from '@mantine/core';
import { FIELD_INPUT_TYPES } from 'constants/menuManagement';

import { NumberInputField } from 'components/MenuManagement/MenuEdit/Fields/NumberInputField';
import type { NumberInputFormValues } from 'components/MenuManagement/MenuEdit/Fields/NumberInputField';
import PriceInputField from 'components/MenuManagement/MenuEdit/Fields/PriceInputField';
import StringField from 'components/MenuManagement/MenuEdit/Fields/StringField';
import ParentLabelField from 'components/MenuManagement/MenuEdit/Fields/ParentLabelField';
import ClassificationsSelectorField from 'components/MenuManagement/MenuEdit/Fields/ClassificationsSelectorField';
import { PhotoThumbnailField } from 'components/MenuManagement/MenuEdit/Fields';
import DropdownSelectorField from 'components/MenuManagement/MenuEdit/Fields/DropdownSelectorField';
import { MultipleModifiersCheckboxField } from 'components/MenuManagement/MenuEdit/Fields/MultipleModifiersCheckboxField';
import type { MultipleModifiersFormValues } from 'components/MenuManagement/MenuEdit/Fields/MultipleModifiersCheckboxField';
import type { UseFormReturnType } from '@mantine/form';

import { AddNewDrawerContext } from 'context/AddNewDrawerContextProvider';
import MenuEdit from 'components/MenuManagement/MenuEdit/MenuEdit';
import type { AddNewDrawerFieldProps } from 'components/MenuManagement/MenuItemActions/types/newDrawerTypes';

const NS = 'menuManagementAddNew';

export const AddNewDrawerField: FunctionComponent<AddNewDrawerFieldProps> = ({ column, form }) => {
  const { t } = useTranslation(NS);
  const { newItemDefaults, parentRecord, setUploadImageFile, isUploading } =
    useContext(AddNewDrawerContext);
  const { isCateringMenu } = useContext(MenuEdit.Context);
  const { inputType, title, dataIndex, text } = column;

  if (dataIndex === 'servingsTag' && !isCateringMenu) {
    return null;
  }

  if (dataIndex === 'imageUrl') {
    return null;
  }

  let content;
  switch (inputType) {
    case FIELD_INPUT_TYPES.NUMBER:
      content = (
        <NumberInputField
          form={form as unknown as UseFormReturnType<NumberInputFormValues>}
          title={title}
          dataIndex={dataIndex ?? ''}
          record={newItemDefaults}
        />
      );
      break;
    case FIELD_INPUT_TYPES.PARENT:
      content = <ParentLabelField form={form} parentType={parentRecord.itemType} />;
      break;
    case FIELD_INPUT_TYPES.PRICE:
      content = (
        <PriceInputField
          form={form}
          title={title}
          dataIndex={dataIndex}
          record={newItemDefaults}
          isNewItem
        />
      );
      break;
    case FIELD_INPUT_TYPES.UPLOAD:
      content = (
        <PhotoThumbnailField
          form={form}
          record={newItemDefaults}
          setUploadImageFile={setUploadImageFile}
          isUploading={isUploading}
          onRemove={() => {
            setUploadImageFile(null);
            form.setFieldValue('imageUrl', '');
          }}
        />
      );
      break;
    case FIELD_INPUT_TYPES.CLASSIFICATIONS:
      content = (
        <ClassificationsSelectorField
          form={form}
          title={title}
          dataIndex={dataIndex}
          record={newItemDefaults}
        />
      );
      break;
    case FIELD_INPUT_TYPES.DROPDOWN_SELECTOR:
      content = (
        <DropdownSelectorField
          form={form}
          text={text}
          dataIndex={dataIndex}
          options={[
            { value: 'true', label: t('AddNewDrawerField.setAsDefaultOption') },
            { value: 'false', label: t('AddNewDrawerField.notDefaultOption') },
          ]}
        />
      );
      break;
    case FIELD_INPUT_TYPES.MULTIPLE_MODIFIERS_CHECKBOX:
      content = (
        <MultipleModifiersCheckboxField
          form={form as unknown as UseFormReturnType<MultipleModifiersFormValues>}
        />
      );
      break;
    default:
      content = <StringField dataIndex={dataIndex} title={title} form={form} />;
  }

  return <Box mb="lg">{content}</Box>;
};
